﻿import { useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaPlay,
    FaDownload,
    FaChartBar,
    FaCalendarDay,
    FaCalendarAlt,
    FaSpinner,
    FaTable,
    FaExclamationTriangle
} from 'react-icons/fa';
import '../../styles/FootfallQueryToolPage.scss';
import * as Sentry from '@sentry/react';

// ────────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────────

const QUERY_TYPES = {
    MONTHLY: 'monthly',
    DAILY: 'daily'
};

const MONTH_OPTIONS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

function getYearOptions() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2015; y--) {
        years.push(y);
    }
    return years;
}

function getDaysInMonth(year, month) {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
}

function formatNumber(val) {
    if (val == null) return '—';
    return Number(val).toLocaleString('en-ZA');
}

/**
 * Converts an array of objects to CSV string.
 */
function jsonToCsv(data) {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const rows = data.map(row =>
        headers.map(h => {
            const val = row[h] ?? '';
            // Escape quotes and wrap in quotes if contains comma or quote
            const str = String(val);
            return str.includes(',') || str.includes('"') || str.includes('\n')
                ? `"${str.replace(/"/g, '""')}"`
                : str;
        }).join(',')
    );
    return [headers.join(','), ...rows].join('\n');
}

function downloadCsv(data, filename) {
    const csv = jsonToCsv(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

// ────────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────────

const FootfallQueryToolPage = () => {
    const { apiFetch } = useAuth();

    // Query configuration
    const [queryType, setQueryType] = useState(QUERY_TYPES.MONTHLY);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [day, setDay] = useState(1);

    // Results
    const [results, setResults] = useState(null);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastQuery, setLastQuery] = useState(null);

    const yearOptions = getYearOptions();
    const maxDay = getDaysInMonth(year, month);

    const executeQuery = useCallback(async () => {
        setLoading(true);
        setError(null);
        setResults(null);
        setColumns([]);

        let url;
        let queryLabel;

        if (queryType === QUERY_TYPES.MONTHLY) {
            url = `/api/RetailDynamics/query/monthly-totals-by-entrance/${year}/${month}`;
            queryLabel = `Monthly Totals by Entrance from ${MONTH_OPTIONS.find(m => m.value === month)?.label} ${year}`;
        } else {
            url = `/api/RetailDynamics/query/daily-totals-by-counting-point/${year}/${month}/${day}`;
            queryLabel = `Daily Totals by Counting Point from ${day} ${MONTH_OPTIONS.find(m => m.value === month)?.label} ${year}`;
        }

        try {
            const response = await apiFetch(url);

            if (!response.ok) {
                let errorMsg = `API returned status ${response.status}`;
                try {
                    const errData = await response.json();
                    if (errData.message) errorMsg = errData.message;
                    if (errData.data?.actionRequired) errorMsg += ` — ${errData.data.actionRequired}`;
                } catch (catchError) {
                    Sentry.captureException(catchError);
                    // Response wasn't JSON
                }
                setError(errorMsg);
                toast.error('Query failed. See error details below.', { autoClose: 5000 });
                return;
            }

            const data = await response.json();

            if (!data || !Array.isArray(data) || data.length === 0) {
                setError('No data returned for the selected parameters. Try a different date range.');
                return;
            }

            // Dynamically detect columns from first row
            const cols = Object.keys(data[0]);
            setColumns(cols);
            setResults(data);
            setLastQuery(queryLabel);
            toast.success(`Query returned ${data.length} record${data.length !== 1 ? 's' : ''}.`, { autoClose: 3000 });
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                throw e;
            }
            console.error('Footfall query error:', e);
            setError('Unable to execute query. Please check your connection and try again.');
            toast.error('Query failed unexpectedly.', { autoClose: 5000 });
        } finally {
            setLoading(false);
        }
    }, [apiFetch, queryType, year, month, day]);

    const handleExportCsv = () => {
        if (!results || results.length === 0) return;
        const prefix = queryType === QUERY_TYPES.MONTHLY ? 'monthly-totals' : 'daily-totals';
        const datePart = queryType === QUERY_TYPES.MONTHLY ? `${year}-${String(month).padStart(2, '0')}` : `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        downloadCsv(results, `footfall-${prefix}-${datePart}.csv`);
        toast.info('CSV exported successfully.', { autoClose: 3000 });
    };

    /**
     * Determines if a column contains numeric data (for right-aligned formatting).
     */
    const isNumericColumn = (colName) => {
        if (!results || results.length === 0) return false;
        // Check first few rows to determine if values are numeric
        const sample = results.slice(0, 5);
        return sample.every(row => {
            const val = row[colName];
            return val === null || val === undefined || typeof val === 'number';
        });
    };

    return (
        <div className="fqt-page">
            {/* Page Header */}
            <div className="fqt-page-header">
                <div className="fqt-header-left">
                    <h1><FaChartBar /> Footfall Query Tool</h1>
                    <p className="fqt-header-desc">
                        Query the Retail Dynamics API to retrieve footfall data for custom date ranges.
                        Select a query type, set your parameters, and execute.
                    </p>
                </div>
            </div>

            {/* Query Builder */}
            <div className="fqt-query-builder">
                <div className="fqt-section-title">Query Configuration</div>

                {/* Query Type Selection */}
                <div className="fqt-type-cards">
                    <button
                        className={`fqt-type-card ${queryType === QUERY_TYPES.MONTHLY ? 'fqt-type-active' : ''}`}
                        onClick={() => setQueryType(QUERY_TYPES.MONTHLY)}
                    >
                        <FaCalendarAlt className="fqt-type-icon" />
                        <div className="fqt-type-info">
                            <div className="fqt-type-name">Monthly Totals by Entrance</div>
                            <div className="fqt-type-desc">
                                Returns monthly footfall totals broken down by entrance, starting from the selected month/year.
                            </div>
                        </div>
                    </button>
                    <button
                        className={`fqt-type-card ${queryType === QUERY_TYPES.DAILY ? 'fqt-type-active' : ''}`}
                        onClick={() => setQueryType(QUERY_TYPES.DAILY)}
                    >
                        <FaCalendarDay className="fqt-type-icon" />
                        <div className="fqt-type-info">
                            <div className="fqt-type-name">Daily Totals by Counting Point</div>
                            <div className="fqt-type-desc">
                                Returns daily footfall totals broken down by entrance, starting from the selected date.
                            </div>
                        </div>
                    </button>
                </div>

                {/* Parameters */}
                <div className="fqt-params">
                    <div className="fqt-param-group">
                        <label className="fqt-label" htmlFor="fqt-year">Year</label>
                        <select
                            id="fqt-year"
                            className="fqt-select"
                            value={year}
                            onChange={e => setYear(Number(e.target.value))}
                        >
                            {yearOptions.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="fqt-param-group">
                        <label className="fqt-label" htmlFor="fqt-month">Month</label>
                        <select
                            id="fqt-month"
                            className="fqt-select"
                            value={month}
                            onChange={e => setMonth(Number(e.target.value))}
                        >
                            {MONTH_OPTIONS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                    {queryType === QUERY_TYPES.DAILY && (
                        <div className="fqt-param-group">
                            <label className="fqt-label" htmlFor="fqt-day">Day</label>
                            <select
                                id="fqt-day"
                                className="fqt-select"
                                value={day > maxDay ? maxDay : day}
                                onChange={e => setDay(Number(e.target.value))}
                            >
                                {Array.from({ length: maxDay }, (_, i) => i + 1).map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="fqt-param-group fqt-param-execute">
                        <button
                            className="fqt-btn-execute"
                            onClick={executeQuery}
                            disabled={loading}
                        >
                            {loading ? <FaSpinner className="fqt-spin" /> : <FaPlay />}
                            {loading ? 'Querying...' : 'Execute Query'}
                        </button>
                    </div>
                </div>

                {/* Endpoint Preview */}
                <div className="fqt-endpoint-preview">
                    <span className="fqt-endpoint-label">API Endpoint:</span>
                    <code className="fqt-endpoint-url">
                        {queryType === QUERY_TYPES.MONTHLY
                            ? `/transposed/monthly-totals-by-entrance/from/${year}/${String(month).padStart(2, '0')}`
                            : `/transposed/daily-totals-by-counting-point/from/${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
                        }
                    </code>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="fqt-error">
                    <FaExclamationTriangle />
                    <div>
                        <strong>Query Error</strong>
                        <p>{error}</p>
                    </div>
                </div>
            )}

            {/* Results */}
            {results && results.length > 0 && (
                <div className="fqt-results">
                    <div className="fqt-results-header">
                        <div className="fqt-results-info">
                            <FaTable />
                            <span className="fqt-results-title">{lastQuery}</span>
                            <span className="fqt-results-count">{results.length} record{results.length !== 1 ? 's' : ''}</span>
                        </div>
                        <button className="fqt-btn-export" onClick={handleExportCsv}>
                            <FaDownload /> Export CSV
                        </button>
                    </div>

                    <div className="fqt-table-wrap">
                        <table className="fqt-table">
                            <thead>
                                <tr>
                                    {columns.map(col => (
                                        <th
                                            key={col}
                                            style={{ textAlign: isNumericColumn(col) ? 'right' : 'left' }}
                                        >
                                            {col.replace(/_/g, ' ')}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((row, rowIdx) => (
                                    <tr key={rowIdx}>
                                        {columns.map(col => (
                                            <td
                                                key={col}
                                                style={{ textAlign: isNumericColumn(col) ? 'right' : 'left' }}
                                            >
                                                {typeof row[col] === 'number'
                                                    ? formatNumber(row[col])
                                                    : (row[col] ?? '—')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FootfallQueryToolPage;